import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { CONFIG, T, telLink, waLink } from "../../data/content";
import { useLang } from "../../context/i18n";
import { MaskLine, Reveal } from "./Motion";
import { track } from "./Chrome";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const titleWords = t(T.heroTitle).split(" ");

  return (
    <section id="home" ref={ref} className="relative overflow-hidden bg-white pt-28 pb-16 lg:pt-36 lg:pb-24 grain">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 lg:grid-cols-2 lg:gap-12 lg:px-8">
        <div className="relative z-10">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-[var(--bg-soft)] px-4 py-1.5 text-sm font-semibold text-[var(--navy)] ring-1 ring-slate-200">
              <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--red)]" /> {t(T.emergency24)}
            </span>
          </Reveal>

          <h1 className="mt-6 font-head text-4xl font-extrabold leading-[1.05] tracking-tighter text-[var(--navy)] sm:text-5xl lg:text-6xl">
            {titleWords.map((w, i) => (
              <MaskLine key={i} delay={0.1 + i * 0.08} className="mr-3 inline-block">{w}</MaskLine>
            ))}
          </h1>

          <Reveal delay={0.5}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[var(--slate)] md:text-lg">{t(T.heroText)}</p>
          </Reveal>

          <Reveal delay={0.65}>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={telLink(CONFIG.phonePrimary)} onClick={() => track("hero_call")} data-testid="hero-call-btn"
                 className="inline-flex items-center gap-2 rounded-full bg-[var(--navy)] px-6 py-3.5 text-sm font-bold text-white shadow-md transition-all hover:scale-[1.03] hover:bg-[var(--navy-light)]">
                <Phone size={18} /> {t(T.callHospital)}
              </a>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" onClick={() => track("hero_whatsapp")} data-testid="hero-whatsapp-btn"
                 className="inline-flex items-center gap-2 rounded-full bg-[var(--green)] px-6 py-3.5 text-sm font-bold text-white shadow-md transition-all hover:scale-[1.03] hover:bg-[var(--green-hover)]">
                <i className="fa-brands fa-whatsapp text-lg" aria-hidden="true"></i> {t(T.whatsappUs)}
              </a>
              <a href={CONFIG.mapsLink} target="_blank" rel="noopener noreferrer" onClick={() => track("hero_directions")} data-testid="hero-directions-btn"
                 className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3.5 text-sm font-bold text-[var(--navy)] ring-1 ring-slate-300 transition-all hover:scale-[1.03] hover:bg-slate-50">
                {t(T.getDirections)} <ArrowRight size={18} />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.8}>
            <div className="mt-6 flex items-center gap-4 text-sm font-semibold text-[var(--slate)]">
              <a href={telLink(CONFIG.phonePrimary)} className="hover:text-[var(--navy)]">+91 {CONFIG.phonePrimary}</a>
              <span className="text-slate-300">|</span>
              <a href={telLink(CONFIG.phoneSecondary)} className="hover:text-[var(--navy)]" data-testid="hero-secondary-phone">+91 {CONFIG.phoneSecondary}</a>
            </div>
          </Reveal>
        </div>

        <motion.div style={{ y }} className="relative">
          <div className="overflow-hidden rounded-3xl ring-1 ring-slate-200 shadow-xl">
            <img src={CONFIG.images.hero} alt="Doctor at Jan Seva Hospital, Gaya consulting patients with family" loading="eager"
                 className="h-full w-full object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-2 rounded-2xl bg-white px-5 py-4 shadow-lg ring-1 ring-slate-200 sm:-left-5">
            <div className="font-head text-2xl font-extrabold text-[var(--navy)]">24×7</div>
            <div className="text-xs font-semibold text-[var(--slate)]">Emergency Care</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
